import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useMachine } from "../contexts/MachineContext";
import { useDatabase } from "../contexts/DatabaseContext";
import { useAuth } from "../contexts/AuthContext";
import { useStorage } from "../contexts/StorageContext";
import { usePrompt } from "../blocker";

import MachinePlayer from "../components/editor/MachinePlayer";
import Canvas from "../components/editor/Canvas";
import CanvasInput from "../components/editor/CanvasInput";
import ContextMenu from "../components/editor/ContextMenu";
import MachineSettings from "../components/editor/MachineSettings";

import utils from "../utils";
import Loading from "../components/Loading";

export default function Editor() {
	const {
		name,
		alphabet,
		states,
		transitions,
		initialStateID,
		acceptStatesIDs,
		addState,
		addTransition,
		renameState,
		renameTransition,
		setInitialState,
		addAcceptState,
		removeAcceptState,
		moveState,
		moveTransition,
		removeState,
		removeTransition,
		setMachine,
	} = useMachine();

	const { machineID } = useParams();
	const navigate = useNavigate();

	const { getFSMImage, uploadFSMImage } = useStorage();
	const { saveFiniteStateMachine, updateFiniteStateMachine, getFSM } =
		useDatabase();
	const { currentUser } = useAuth();

	const [focusedItem, setFocusedItem] = useState();
	const [showSettings, setShowSettings] = useState(false);
	const [showPlayer, setShowPlayer] = useState(false);

	// Canvas HTML element for taking screenshots
	const [canvas, setCanvas] = useState();

	const canvasInputRef = useRef();
	const [inputValue, setInputValue] = useState("----");
	const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
	const [editing, setEditing] = useState(false);

	const contextMenuRef = useRef();

	const [creatingTransition, setCreatingTransition] = useState(false);

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	usePrompt(
		"¿Seguro deseas salir? Se perderán los cambios no guardados.",
		!saving
	);

	useEffect(() => {
		window.addEventListener("keypress", onKeyPress);
	}, []);

	useEffect(() => {
		if (canvas) {
			canvas.onBeforeUnload = onBeforeUnload;
		}
	}, [canvas]);

	useEffect(() => {
		if (currentUser && machineID) {
			LoadMachine();
		}
	}, [currentUser]);

	//#region CLICK

	function whenClicked() {
		setCreatingTransition(false);
		hideContextMenu();
	}
	function whenClickedNode() {}
	function whenClickedControlPoint() {}

	function whenContextMenu(item) {
		if (item) {
			setFocusedItem({ ...item });
			showContextMenu(item.x, item.y);
		} else {
			setFocusedItem(null);
			hideContextMenu();
		}
	}
	//#endregion

	//#region MOVE

	function whenMovedNode(id, x, y) {
		moveState(id, x, y);
	}
	function whenMovedControlPoint(id, x, y) {
		moveTransition(id, x, y);
	}

	//#endregion

	//#region CREATE

	function whenCreatedNewNode(x, y) {
		const lastState = states[states.length - 1];
		const stateName = "e" + lastState.id;

		const state = {
			id: lastState.id + 1,
			name: stateName,
			x: x,
			y: y,
			transitions: [],
		};

		addState(state);
		return state;
	}
	function whenCreatedNewTransition(startNodeID, endNodeID) {
		const startIndex = utils.IDToIndex(states, startNodeID);
		const endIndex = utils.IDToIndex(states, endNodeID);
		if (startIndex === -1 || endIndex === -1) return;

		const startingNode = states[startIndex];
		const endingNode = states[endIndex];

		const [controlPointX, controlPointY] = utils.getControlPointPosition(
			startingNode.x,
			startingNode.y,
			endingNode.x,
			endingNode.y
		);

		let lastId = 0;

		if (transitions.length > 0) {
			lastId = transitions[transitions.length - 1].id;
		}

		const transition = {
			id: lastId + 1,
			start: startNodeID,
			end: endNodeID,
			x: controlPointX,
			y: controlPointY,
			symbols: [alphabet[0]],
		};

		addTransition(transition);
	}

	//#endregion

	//#region EDIT

	function whenEditing(x, y, item) {
		setFocusedItem({ x: x, y: y, ...item });
		hideContextMenu();

		if (item) {
			showCanvasInput(x, y, item);
		} else {
			hideCanvasInput();
		}
	}

	function editNode(id, name) {
		renameState(id, name);
	}
	function editTransition(id, symbols) {
		renameTransition(id, symbols);
	}

	function hideCanvasInput() {
		setEditing(false);
	}

	function showCanvasInput(x, y, item) {
		let defaultValue = "";
		switch (item.type) {
			case utils.NODE:
				defaultValue = states[utils.IDToIndex(states, item.id)].name;
				break;
			case utils.CONTROL_POINT:
				defaultValue =
					transitions[utils.IDToIndex(transitions, item.id)].symbols;
				break;
			default:
				defaultValue = "";
				break;
		}

		setEditing(true);
		setInputPosition({ x: x, y: y });
		setInputValue(defaultValue);
	}

	//#endregion

	//#region EVENTS
	function onKeyPress(event) {
		// User was editing and pressed enter key to confirm changes
		if (event.key == "Enter") {
			hideCanvasInput();
		}
	}
	function onCanvasInputChange(event) {
		if (focusedItem) {
			switch (focusedItem.type) {
				case utils.NODE:
					editNode(focusedItem.id, event.target.value);
					break;
				case utils.CONTROL_POINT:
					editTransition(focusedItem.id, event.target.value);
					break;
				default:
					break;
			}
		}
		setInputValue(event.target.value);
	}
	function onBeforeUnload(event) {
		event.preventDefault();
		return "AA";
	}
	//#endregion

	//#region CONTEXT MENU
	function showContextMenu(x, y) {
		x += contextMenuRef.current.clientWidth / 2;
		y += contextMenuRef.current.clientHeight / 2;
		contextMenuRef.current.style.left = x + "px";
		contextMenuRef.current.style.top = y + "px";

		contextMenuRef.current.style.opacity = 1;
		contextMenuRef.current.style.pointerEvents = "all";
	}
	function hideContextMenu() {
		contextMenuRef.current.style.opacity = 0;
		contextMenuRef.current.style.pointerEvents = "none";
	}

	function SetInitialState() {
		focusedItem.accept = true;
		setInitialState(focusedItem.id);
	}
	function AddAcceptState() {
		focusedItem.accept = true;
		addAcceptState(focusedItem.id);
	}
	function RemoveAcceptState() {
		focusedItem.accept = false;
		removeAcceptState(focusedItem.id);
	}

	function DeleteItem() {
		switch (focusedItem.type) {
			case utils.NODE:
				removeState(focusedItem.id);
				break;
			case utils.CONTROL_POINT:
				removeTransition(focusedItem.id);
				break;

			default:
				break;
		}
		hideContextMenu();
	}
	//#endregion

	//#region CRUD

	async function getMachineData() {
		const imageUrl = await UploadCanvasPreviewImage();
		return {
			userUID: currentUser.uid,
			name: name,
			states: states,
			transitions: transitions,
			alphabet: alphabet,
			initialStateID: initialStateID,
			acceptStatesIDs: acceptStatesIDs,
			previewImage: imageUrl,
		};
	}

	function SaveChanges() {
		if (!currentUser) return;
		if (machineID) {
			UpdateMachine();
		} else {
			SaveMachine();
		}
	}
	async function SaveMachine() {
		setSaving(true);

		const data = await getMachineData();
		saveFiniteStateMachine(data)
			.then((result) => {
				navigate("/editor/" + result.id);

				setSaving(false);
			})
			.catch((error) => {
				console.error({ ...error });

				setSaving(false);
			});
	}
	async function UpdateMachine() {
		setSaving(true);

		const data = await getMachineData();
		updateFiniteStateMachine(machineID, data)
			.then((result) => {
				setSaving(false);
			})
			.catch((error) => {
				console.error({ ...error });
				setSaving(false);
			});
	}

	async function LoadMachine() {
		setLoading(true);
		const machine = await getFSM(machineID);
		setMachine(
			machine.states,
			machine.transitions,
			machine.initialStateID,
			machine.acceptStatesIDs,
			machine.name
		);
		setLoading(false);
	}

	//#endregion

	//#region CANVAS SCREENSHOT

	async function UploadCanvasPreviewImage() {
		const dataURL = canvas.toDataURL("png");
		const imageName = machineID + "-preview-image.png";
		let imageUrl = "";
		await uploadFSMImage(dataURL, imageName).then(async (snapshot) => {
			await getFSMImage(imageName).then((url) => {
				imageUrl = url;
			});
		});

		return imageUrl;
	}

	//#endregion

	return (
		<div className="editor">
			{loading ? (
				<Loading overlay={true} description="Cargando máquina" />
			) : null}
			{saving ? (
				<Loading overlay={true} description="Guardando cambios" />
			) : null}
			<div className="canvas-wrapper">
				<Canvas
					setCanvas={setCanvas}
					alphabet={[...alphabet]}
					states={[...states]}
					transitions={[...transitions]}
					initialStateID={initialStateID}
					acceptStatesIDs={[...acceptStatesIDs]}
					whenClicked={whenClicked}
					whenClickedNode={whenClickedNode}
					whenClickedControlPoint={whenClickedControlPoint}
					whenMovedNode={whenMovedNode}
					whenMovedControlPoint={whenMovedControlPoint}
					whenCreatedNewNode={whenCreatedNewNode}
					whenCreatedNewTransition={whenCreatedNewTransition}
					whenEditing={whenEditing}
					whenContextMenu={whenContextMenu}
					creatingTransition={creatingTransition}
				/>
				<CanvasInput
					position={inputPosition}
					active={editing}
					onInputChange={onCanvasInputChange}
					value={inputValue}
				/>
				<ContextMenu
					menuRef={contextMenuRef}
					item={focusedItem}
					setInitialState={SetInitialState}
					addAcceptState={AddAcceptState}
					removeAcceptState={RemoveAcceptState}
					editItem={() => {
						showCanvasInput(
							focusedItem.x,
							focusedItem.y,
							focusedItem
						);
						hideContextMenu();
					}}
					createTransition={() => {
						setCreatingTransition(true);
						hideContextMenu();
					}}
					deleteItem={DeleteItem}
				/>
			</div>
			<MachinePlayer show={showPlayer} />
			<MachineSettings
				show={showSettings}
				name={name}
				alphabet={alphabet}
				states={states}
				initialStateID={initialStateID}
				acceptStatesIDs={acceptStatesIDs}
			/>
			<div className="editor-actions">
				<button
					className="toggle-button button-left-icon"
					onClick={() => setShowPlayer(!showPlayer)}
				>
					<div className="icon">play_arrow</div>
					<p className="text">Ejecutar</p>
				</button>
				<button
					className="toggle-button button-left-icon"
					onClick={SaveChanges}
				>
					<div className="icon">save</div>
					<p className="text">Guardar cambios</p>
				</button>
				<button
					className="toggle-button button-left-icon"
					onClick={() => setShowSettings(!showSettings)}
				>
					<div className="icon">settings</div>
					<p className="text">Configurar</p>
				</button>
			</div>
		</div>
	);
}
